import { askCarli } from './carliAgent'

// The report structure Carli is trained to produce for an inventory lot. She
// returns it as JSON so the portal can render it in the standard format rather
// than dumping prose.
export type FactorStrength = 'Strong' | 'Positive' | 'Moderate' | 'Limited' | 'Weak'

export type CarliReport = {
  headline: { low: number; expected: number; high: number; aur: number; confidence: string; statement: string }
  outlook: { label: string; value: number; note: string }[]
  factors: { name: string; strength: FactorStrength; findings: string[]; takeaway: string }[]
  condition: { rating: string; notes: string[] }
  dataIntegrity: { rating: string; notes: string[] }
  modelConfidence: { level: string; known: string[]; estimated: string[]; unknown: string[] }
  supports: { title: string; detail: string }[]
  holdingBack: { title: string; detail: string }[]
  lifts: { title: string; detail: string }[]
  risks: { title: string; level: string; detail: string }[]
  conclusion: string
}

const REPORT_INSTRUCTION = `Produce your standard Carli Recovery Report for the uploaded lot below.

Ground every estimate in the realized sales history in your workspace context — actual average unit revenue, recovery against MSRP, and category sell-through from comparable inventory already selling on the platform. The modeled figures in the lot summary are a starting assumption, not a result. If the realized data implies a different recovery rate or AUR, use your own number and say why in the relevant finding.

Return ONLY a JSON object, no prose, no markdown fences, in exactly this shape:

{
  "headline": { "low": number, "expected": number, "high": number, "aur": number, "confidence": "Low|Medium|High", "statement": "one sentence framing the range" },
  "outlook": [
    { "label": "Conservative", "value": number, "note": "what has to be true" },
    { "label": "Expected", "value": number, "note": "what has to be true" },
    { "label": "Upside", "value": number, "note": "what has to be true" }
  ],
  "factors": [
    { "name": "Market pricing", "strength": "Strong|Positive|Moderate|Limited|Weak", "findings": ["2-4 short factual findings with numbers"], "takeaway": "one sentence" }
  ],
  "condition": { "rating": "Strong|Moderate|Limited", "notes": ["2-3 short notes"] },
  "dataIntegrity": { "rating": "Strong|Moderate|Limited", "notes": ["2-3 short notes"] },
  "modelConfidence": { "level": "Low|Medium|High", "known": ["..."], "estimated": ["..."], "unknown": ["..."] },
  "supports": [{ "title": "short label", "detail": "one sentence" }],
  "holdingBack": [{ "title": "short label", "detail": "one sentence" }],
  "lifts": [{ "title": "short label", "detail": "one sentence" }],
  "risks": [{ "title": "short label", "level": "Low|Moderate|High", "detail": "one sentence" }],
  "conclusion": "two or three sentences stating the expected recovery, the AUR, and what to do first"
}

Rules: six factors covering market pricing, product mix, size distribution, materials and construction, seasonality, and marketplace demand. Three to six entries in supports, holdingBack, lifts, and risks. All money values are plain numbers in dollars, no symbols or commas. Never invent figures that are not in the lot summary or your workspace context.`

function extractJson(text: string) {
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/)
  const candidate = fenced ? fenced[1] : text
  const start = candidate.indexOf('{')
  const end = candidate.lastIndexOf('}')
  if (start === -1 || end === -1) throw new Error('No report returned')
  return JSON.parse(candidate.slice(start, end + 1)) as CarliReport
}

export async function requestCarliReport({
  profile,
  portalContext,
}: {
  profile: string
  portalContext?: string
}): Promise<CarliReport> {
  const response = await askCarli({
    messages: [{ role: 'user', content: `${REPORT_INSTRUCTION}\n\nUPLOADED LOT\n${profile}` }],
    context: portalContext,
    maxTokens: 4000,
  })
  return extractJson(response)
}
